/**
 * Enhanced Business Hours Service
 * Week 3: 高度な営業時間管理サービス
 * 
 * 特別営業日・祝日・季節営業時間の解決と管理
 */

import logger from '../utils/logger';
import {
  EnhancedBusinessHours,
  DailySchedule,
  SpecialBusinessDay,
  SeasonalHours,
  Holiday,
  RecurringException,
  ResolvedSchedule,
  BusinessHoursValidation,
  BusinessHoursStats,
  DEFAULT_ENHANCED_BUSINESS_HOURS,
} from '../types/businessHours';
import { useStore } from '../store/useStore';

class BusinessHoursService {
  private businessHours: EnhancedBusinessHours = DEFAULT_ENHANCED_BUSINESS_HOURS;
  private initialized = false;
  private unsubscribe?: () => void;

  // =============================================================================
  // 🔧 初期化・設定管理
  // =============================================================================

  async initialize(): Promise<void> {
    if (this.initialized) return;

    try {
      // Storeから営業時間データを取得
      await this.loadFromStore();
      // Store変更の監視を開始
      this.subscribeToStoreChanges();
      this.initialized = true;
      logger.log('✅ BusinessHoursService initialized with store data');
    } catch (error) {
      logger.error('❌ BusinessHoursService initialization failed:', error);
      // フォールバック: デフォルトデータを使用
      await this.setupSampleData();
      this.initialized = true;
      logger.log('⚠️ Using fallback sample data');
    }
  }

  private async loadFromStore(): Promise<void> {
    const store = useStore.getState();
    const storeBusinessHours = store.settings?.store?.businessHours;

    if (storeBusinessHours) {
      // Store形式からEnhancedBusinessHours形式に変換
      this.businessHours.defaultWeeklySchedule = this.convertStoreToDefaultSchedule(storeBusinessHours) as any;
      logger.log('📦 Loaded business hours from store');
    } else {
      // Storeにデータがない場合はデフォルトを使用
      logger.log('⚠️ No business hours in store, using defaults');
    }
  }

  private convertStoreToDefaultSchedule(storeHours: any): Record<string, DailySchedule> {
    const schedule: Record<string, DailySchedule> = {};
    const dayMapping: Record<string, string> = {
      '月曜日': 'monday',
      '火曜日': 'tuesday',
      '水曜日': 'wednesday',
      '木曜日': 'thursday',
      '金曜日': 'friday',
      '土曜日': 'saturday',
      '日曜日': 'sunday'
    };

    Object.entries(storeHours).forEach(([key, value]: [string, any]) => {
      const dayKey = dayMapping[key] || key;
      if (value && typeof value === 'object') {
        schedule[dayKey] = {
          isOpen: value.isOpen || false,
          timeSlots: value.openTime && value.closeTime ? [
            { start: value.openTime, end: value.closeTime }
          ] : [],
          breaks: value.breakStart && value.breakEnd ? [
            {
              id: `break-${dayKey}`,
              start: value.breakStart,
              end: value.breakEnd,
              name: '休憩時間',
              recurring: false
            }
          ] : [],
          maxCapacity: value.maxCapacity || 10,
          notes: value.notes || ''
        };
      }
    });

    return schedule;
  }

  private subscribeToStoreChanges(): void {
    // Store変更の監視
    this.unsubscribe = useStore.subscribe((state) => {
      const businessHours = state.settings?.store?.businessHours;
      if (businessHours) {
        this.businessHours.defaultWeeklySchedule = this.convertStoreToDefaultSchedule(businessHours) as any;
        logger.log('🔄 Business hours updated from store');
      }
    });
  }

  refresh(): void {
    // 外部から強制的に再読み込み
    this.loadFromStore();
  }

  cleanup(): void {
    // サービスのクリーンアップ時に監視を解除
    if (this.unsubscribe) {
      this.unsubscribe();
      this.unsubscribe = undefined;
    }
    this.initialized = false;
  }

  private async setupSampleData(): Promise<void> {
    // 祝日サンプルデータ
    const sampleHolidays: Holiday[] = [
      {
        id: 'holiday-001',
        name: '元日',
        date: '2024-01-01',
        type: 'national',
        businessRule: 'closed',
        isRecurring: true,
        recurringRule: {
          type: 'fixed_date',
          month: 1,
          day: 1,
        },
        customerMessage: '新年は元日のみお休みをいただきます',
        isPublicHoliday: true,
      },
      {
        id: 'holiday-002',
        name: 'ゴールデンウィーク',
        date: '2024-05-03',
        type: 'national',
        businessRule: 'special_hours',
        specialSchedule: {
          isOpen: true,
          timeSlots: [{ start: '11:00', end: '18:00' }],
          breaks: [],
          maxCapacity: 6,
        },
        isRecurring: true,
        recurringRule: {
          type: 'fixed_date',
          month: 5,
          day: 3,
        },
        customerMessage: 'GW期間は短縮営業いたします',
        isPublicHoliday: true,
      },
    ];

    // 特別営業日サンプルデータ
    const sampleSpecialDays: SpecialBusinessDay[] = [
      {
        id: 'special-001',
        date: '2024-12-31',
        type: 'special_hours',
        schedule: {
          isOpen: true,
          timeSlots: [{ start: '10:00', end: '16:00' }],
          breaks: [],
          maxCapacity: 3,
          notes: '年末最終営業日',
        },
        reason: '年末特別営業',
        description: '年末最終営業日のため短縮営業いたします',
        customerMessage: '年末は16時までの営業となります',
        allowReservations: true,
        maxReservations: 20,
        createdAt: new Date(),
        updatedAt: new Date(),
        createdBy: 'system',
      },
    ];

    // 季節営業時間サンプルデータ
    const sampleSeasonalHours: SeasonalHours[] = [
      {
        id: 'seasonal-001',
        name: '夏季営業時間',
        startDate: '2024-07-01',
        endDate: '2024-08-31',
        isYearlyRecurring: true,
        weeklySchedule: {
          ...DEFAULT_ENHANCED_BUSINESS_HOURS.defaultWeeklySchedule,
          monday: { 
            ...DEFAULT_ENHANCED_BUSINESS_HOURS.defaultWeeklySchedule.monday,
            isOpen: false,
            isHoliday: true,
            holidayReason: '定休日'
          },
          tuesday: {
            isOpen: true,
            timeSlots: [{ start: '09:00', end: '21:00' }],
            breaks: [
              {
                id: 'summer-break-1',
                name: '昼休み',
                start: '13:00',
                end: '14:00',
                recurring: true,
              }
            ],
            maxCapacity: 5,
          },
          // ... 他の曜日も夏季時間で設定
        },
        priority: 1,
        description: '夏季期間の延長営業',
        customerMessage: '夏季期間は営業時間を延長しています',
        isActive: true,
        createdAt: new Date(),
        updatedAt: new Date(),
      },
    ];

    this.businessHours = {
      ...DEFAULT_ENHANCED_BUSINESS_HOURS,
      holidays: sampleHolidays,
      specialDays: sampleSpecialDays,
      seasonalHours: sampleSeasonalHours,
    };
  }

  // =============================================================================
  // 📅 営業時間解決
  // =============================================================================

  /**
   * 指定日の営業時間を解決
   */
  resolveScheduleForDate(date: string): ResolvedSchedule {
    const parsedDate = new Date(date);
    
    // 1. 特別営業日チェック
    const specialDay = this.businessHours.specialDays.find(sd => sd.date === date);
    if (specialDay && specialDay.schedule) {
      return {
        date,
        schedule: specialDay.schedule,
        source: 'special_day',
        reason: specialDay.reason,
      };
    }

    // 2. 祝日チェック
    const holiday = this.businessHours.holidays.find(h => 
      h.date === date || (h.isRecurring && this.isHolidayDate(parsedDate, h))
    );
    if (holiday) {
      if (holiday.businessRule === 'closed') {
        return {
          date,
          schedule: {
            isOpen: false,
            timeSlots: [],
            breaks: [],
            isHoliday: true,
            holidayReason: holiday.name,
          },
          source: 'holiday',
          reason: holiday.name,
        };
      } else if (holiday.businessRule === 'special_hours' && holiday.specialSchedule) {
        return {
          date,
          schedule: holiday.specialSchedule,
          source: 'holiday',
          reason: `${holiday.name}特別営業`,
        };
      }
    }

    // 3. 季節営業時間チェック
    const seasonalHours = this.businessHours.seasonalHours
      .filter(sh => sh.isActive && this.isDateInRange(date, sh.startDate, sh.endDate))
      .sort((a, b) => b.priority - a.priority)[0];
    
    if (seasonalHours) {
      const dayOfWeek = this.getDayOfWeek(parsedDate);
      const schedule = seasonalHours.weeklySchedule[dayOfWeek as keyof typeof seasonalHours.weeklySchedule];
      return {
        date,
        schedule,
        source: 'seasonal',
        reason: seasonalHours.name,
      };
    }

    // 4. 繰り返し例外チェック
    const recurringException = this.businessHours.recurringExceptions.find(re => 
      re.isActive && this.matchesRecurringPattern(parsedDate, re)
    );
    if (recurringException) {
      return {
        date,
        schedule: recurringException.override,
        source: 'recurring_exception',
        reason: recurringException.name,
      };
    }

    // 5. デフォルト営業時間
    const dayOfWeek = this.getDayOfWeek(parsedDate);
    const defaultSchedule = this.businessHours.defaultWeeklySchedule[dayOfWeek as keyof typeof this.businessHours.defaultWeeklySchedule];
    
    return {
      date,
      schedule: defaultSchedule,
      source: 'default',
    };
  }

  /**
   * 期間内の営業時間解決
   */
  resolveScheduleForRange(startDate: string, endDate: string): ResolvedSchedule[] {
    const schedules: ResolvedSchedule[] = [];
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    for (let date = new Date(start); date <= end; date.setDate(date.getDate() + 1)) {
      const dateString = date.toISOString().split('T')[0];
      schedules.push(this.resolveScheduleForDate(dateString));
    }
    
    return schedules;
  }

  // =============================================================================
  // ⏰ 営業時間チェック
  // =============================================================================

  /**
   * 指定時刻が営業時間内かチェック
   */
  isOpenAtTime(date: string, time: string): boolean {
    const schedule = this.resolveScheduleForDate(date);
    
    if (!schedule.schedule.isOpen) return false;
    
    // 営業時間内チェック
    const timeInMinutes = this.timeToMinutes(time);
    
    for (const slot of schedule.schedule.timeSlots) {
      const startMinutes = this.timeToMinutes(slot.start);
      const endMinutes = this.timeToMinutes(slot.end);
      
      if (timeInMinutes >= startMinutes && timeInMinutes <= endMinutes) {
        // 休憩時間でないかチェック
        const isBreakTime = schedule.schedule.breaks.some(breakTime => {
          const breakStart = this.timeToMinutes(breakTime.start);
          const breakEnd = this.timeToMinutes(breakTime.end);
          return timeInMinutes >= breakStart && timeInMinutes <= breakEnd;
        });
        
        if (!isBreakTime) return true;
      }
    }
    
    return false;
  }

  /**
   * 指定期間の営業日数を取得
   */
  getOperatingDaysCount(startDate: string, endDate: string): number {
    const schedules = this.resolveScheduleForRange(startDate, endDate);
    return schedules.filter(s => s.schedule.isOpen).length;
  }

  /**
   * 次の営業日を取得
   */
  getNextOperatingDay(fromDate: string): string | null {
    const currentDate = new Date(fromDate);
    currentDate.setDate(currentDate.getDate() + 1);
    
    // 最大30日先まで検索
    for (let i = 0; i < 30; i++) {
      const dateString = currentDate.toISOString().split('T')[0];
      const schedule = this.resolveScheduleForDate(dateString);
      
      if (schedule.schedule.isOpen) {
        return dateString;
      }
      
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    return null;
  }

  // =============================================================================
  // 📊 統計・分析
  // =============================================================================

  /**
   * 営業時間統計を取得
   */
  getBusinessHoursStats(startDate: string, endDate: string): BusinessHoursStats {
    const schedules = this.resolveScheduleForRange(startDate, endDate);
    
    const operatingSchedules = schedules.filter(s => s.schedule.isOpen);
    const totalHours = operatingSchedules.reduce((total, schedule) => {
      return total + this.calculateDailyOperatingHours(schedule.schedule);
    }, 0);
    
    // 曜日別統計
    const dayStats = new Map<string, { hours: number; count: number }>();
    operatingSchedules.forEach(schedule => {
      const dayOfWeek = this.getDayOfWeek(new Date(schedule.date));
      const hours = this.calculateDailyOperatingHours(schedule.schedule);
      
      if (!dayStats.has(dayOfWeek)) {
        dayStats.set(dayOfWeek, { hours: 0, count: 0 });
      }
      
      const stats = dayStats.get(dayOfWeek)!;
      stats.hours += hours;
      stats.count += 1;
    });
    
    // 最も忙しい/暇な曜日を特定
    let mostBusyDay = '';
    let leastBusyDay = '';
    let maxAvgHours = 0;
    let minAvgHours = 24;
    
    dayStats.forEach((stats, day) => {
      const avgHours = stats.hours / stats.count;
      if (avgHours > maxAvgHours) {
        maxAvgHours = avgHours;
        mostBusyDay = day;
      }
      if (avgHours < minAvgHours) {
        minAvgHours = avgHours;
        leastBusyDay = day;
      }
    });
    
    return {
      totalOperatingHours: totalHours,
      averageHoursPerDay: totalHours / operatingSchedules.length,
      mostBusyDay,
      leastBusyDay,
      totalHolidays: schedules.filter(s => s.schedule.isHoliday).length,
      specialDaysCount: schedules.filter(s => s.source === 'special_day').length,
      monthlyStats: [], // TODO: 月別統計の実装
    };
  }

  // =============================================================================
  // 🔧 バリデーション
  // =============================================================================

  /**
   * 営業時間設定の妥当性を検証
   */
  validateBusinessHours(): BusinessHoursValidation {
    const errors: string[] = [];
    const warnings: string[] = [];

    // 1. 基本営業時間の検証
    Object.entries(this.businessHours.defaultWeeklySchedule).forEach(([day, schedule]) => {
      if (schedule.isOpen && schedule.timeSlots.length === 0) {
        errors.push(`${day}: 営業日なのに営業時間が設定されていません`);
      }
      
      schedule.timeSlots.forEach((slot: any, index: number) => {
        if (this.timeToMinutes(slot.start) >= this.timeToMinutes(slot.end)) {
          errors.push(`${day}: 営業時間${index + 1}の開始時間が終了時間以降です`);
        }
      });
    });

    // 2. 特別営業日の検証
    this.businessHours.specialDays.forEach(specialDay => {
      if (specialDay.allowReservations && !specialDay.schedule?.isOpen) {
        warnings.push(`特別営業日 ${specialDay.date}: 休業日なのに予約を受け付ける設定です`);
      }
    });

    // 3. 季節営業時間の重複検証
    const activeSeasonal = this.businessHours.seasonalHours.filter(sh => sh.isActive);
    for (let i = 0; i < activeSeasonal.length; i++) {
      for (let j = i + 1; j < activeSeasonal.length; j++) {
        const seasonal1 = activeSeasonal[i];
        const seasonal2 = activeSeasonal[j];
        
        if (this.isRangeOverlap(seasonal1.startDate, seasonal1.endDate, seasonal2.startDate, seasonal2.endDate)) {
          warnings.push(`季節営業時間が重複しています: ${seasonal1.name} と ${seasonal2.name}`);
        }
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings,
    };
  }

  // =============================================================================
  // 🛠️ ユーティリティメソッド
  // =============================================================================

  private getDayOfWeek(date: Date): string {
    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    return days[date.getDay()];
  }

  private timeToMinutes(time: string): number {
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
  }

  private calculateDailyOperatingHours(schedule: DailySchedule): number {
    if (!schedule.isOpen) return 0;
    
    let totalMinutes = 0;
    
    // 営業時間の合計
    schedule.timeSlots.forEach(slot => {
      const startMinutes = this.timeToMinutes(slot.start);
      const endMinutes = this.timeToMinutes(slot.end);
      totalMinutes += endMinutes - startMinutes;
    });
    
    // 休憩時間を引く
    schedule.breaks.forEach(breakTime => {
      const startMinutes = this.timeToMinutes(breakTime.start);
      const endMinutes = this.timeToMinutes(breakTime.end);
      totalMinutes -= endMinutes - startMinutes;
    });
    
    return totalMinutes / 60;
  }

  private isDateInRange(date: string, startDate: string, endDate: string): boolean {
    const checkDate = new Date(date);
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    return checkDate >= start && checkDate <= end;
  }

  private isRangeOverlap(start1: string, end1: string, start2: string, end2: string): boolean {
    const s1 = new Date(start1);
    const e1 = new Date(end1);
    const s2 = new Date(start2);
    const e2 = new Date(end2);
    
    return s1 <= e2 && s2 <= e1;
  }

  private isHolidayDate(date: Date, holiday: Holiday): boolean {
    if (!holiday.isRecurring || !holiday.recurringRule) return false;
    
    const rule = holiday.recurringRule;
    
    switch (rule.type) {
      case 'fixed_date':
        return date.getMonth() === (rule.month! - 1) && date.getDate() === rule.day!;
      
      case 'nth_weekday':
        // 第n週の特定曜日の実装（例：第2月曜日）
        return this.isNthWeekdayOfMonth(date, rule.weekOfMonth!, rule.dayOfWeek!, rule.month! - 1);
      
      default:
        return false;
    }
  }

  private isNthWeekdayOfMonth(date: Date, weekOfMonth: number, dayOfWeek: number, month: number): boolean {
    if (date.getMonth() !== month || date.getDay() !== dayOfWeek) return false;
    
    const firstDay = new Date(date.getFullYear(), month, 1);
    const firstWeekday = new Date(date.getFullYear(), month, 1 + (7 + dayOfWeek - firstDay.getDay()) % 7);
    const targetDate = new Date(firstWeekday.getTime() + (weekOfMonth - 1) * 7 * 24 * 60 * 60 * 1000);
    
    return date.getTime() === targetDate.getTime();
  }

  private matchesRecurringPattern(date: Date, exception: RecurringException): boolean {
    // 繰り返しパターンの実装（簡略版）
    const rule = exception.recurrence;
    
    switch (exception.pattern) {
      case 'weekly':
        return rule.dayOfWeek !== undefined && date.getDay() === rule.dayOfWeek;
      
      case 'monthly':
        return rule.dayOfMonth !== undefined && date.getDate() === rule.dayOfMonth;
      
      default:
        return false;
    }
  }

  // =============================================================================
  // 🔄 CRUD操作
  // =============================================================================

  async updateBusinessHours(updates: Partial<EnhancedBusinessHours>): Promise<boolean> {
    try {
      this.businessHours = { ...this.businessHours, ...updates };
      this.businessHours.lastUpdated = new Date();
      
      logger.log('✅ Business hours updated successfully');
      return true;
    } catch (error) {
      logger.error('❌ Failed to update business hours:', error);
      return false;
    }
  }

  getBusinessHours(): EnhancedBusinessHours {
    return { ...this.businessHours };
  }
}

// シングルトンインスタンス
export const businessHoursService = new BusinessHoursService();
export default businessHoursService;